import React, { useState } from 'react';
import { 
  Download, 
  Calendar, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  ShoppingBag,
  Users,
  Package,
  BarChart3,
  PieChart,
  LineChart,
  Activity,
  Filter,
  ChevronDown,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Award,
  Star,
  CreditCard,
  Truck,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import Layout from '../components/Layout';

const Reports = () => {
  const [dateRange, setDateRange] = useState('This Month');
  const [reportType, setReportType] = useState('Sales');
  const [selectedReport, setSelectedReport] = useState('overview');

  const dateOptions = ['Today', 'This Week', 'This Month', 'This Quarter', 'This Year', 'Custom'];

  // Sales Data
  const salesData = {
    totalRevenue: 128450,
    totalOrders: 258,
    averageOrder: 498,
    totalProducts: 326,
    totalCustomers: 158,
    conversionRate: '2.4%',
    revenueChange: '+18%',
    ordersChange: '+12%'
  };

  // Monthly Sales Chart Data
  const monthlySales = [
    { month: 'Jan', revenue: 65000, orders: 130, profit: 25000 },
    { month: 'Feb', revenue: 78000, orders: 156, profit: 31000 },
    { month: 'Mar', revenue: 90000, orders: 180, profit: 36000 },
    { month: 'Apr', revenue: 85000, orders: 170, profit: 34000 },
    { month: 'May', revenue: 72000, orders: 144, profit: 28000 },
    { month: 'Jun', revenue: 95000, orders: 190, profit: 38000 },
    { month: 'Jul', revenue: 88000, orders: 176, profit: 35000 },
    { month: 'Aug', revenue: 70000, orders: 140, profit: 28000 },
    { month: 'Sep', revenue: 92000, orders: 184, profit: 37000 },
    { month: 'Oct', revenue: 80000, orders: 160, profit: 32000 },
    { month: 'Nov', revenue: 75000, orders: 150, profit: 30000 },
    { month: 'Dec', revenue: 82000, orders: 164, profit: 33000 }
  ];

  // Top Products
  const topProducts = [
    { name: 'Majun Arad Khurma', sales: 452, revenue: 113000, growth: '+25%' },
    { name: 'Sharbat Faulad', sales: 380, revenue: 83600, growth: '+18%' },
    { name: 'Habib-e-Sudanshan', sales: 295, revenue: 53100, growth: '+12%' },
    { name: 'Majun Shabab', sales: 210, revenue: 63000, growth: '+8%' },
    { name: 'Rogan Badam', sales: 185, revenue: 83250, growth: '+15%' }
  ];

  // Category Performance
  const categoryPerformance = [
    { name: 'Unani Medicines', revenue: 45000, orders: 90, growth: '+22%' },
    { name: 'Ayurvedic Medicines', revenue: 38000, orders: 76, growth: '+18%' },
    { name: 'Health Care', revenue: 22000, orders: 44, growth: '+12%' },
    { name: 'Syrups & Juices', revenue: 15000, orders: 30, growth: '+8%' },
    { name: 'Capsules & Tablets', revenue: 28000, orders: 56, growth: '+15%' }
  ];

  // Payment Methods
  const paymentMethods = [
    { method: 'UPI', percentage: 35, amount: 44957 },
    { method: 'Card', percentage: 28, amount: 35966 },
    { method: 'Cash on Delivery', percentage: 22, amount: 28259 },
    { method: 'Net Banking', percentage: 15, amount: 19268 }
  ];

  // Order Status Distribution
  const orderStatus = [
    { status: 'Delivered', count: 145, percentage: 56 },
    { status: 'Out for Delivery', count: 48, percentage: 19 },
    { status: 'Confirmed', count: 35, percentage: 14 },
    { status: 'Canceled', count: 30, percentage: 11 }
  ];

  // Customer Demographics
  const customerDemographics = [
    { type: 'New Customers', count: 42, percentage: 27 },
    { type: 'Returning Customers', count: 82, percentage: 52 },
    { type: 'VIP Customers', count: 34, percentage: 21 }
  ];

  const getGrowthColor = (growth) => {
    return growth.startsWith('+') ? 'var(--color-success)' : 'var(--color-danger)';
  };

  const formatCurrency = (amount) => {
    return '₹' + amount.toLocaleString();
  };

  // Calculate max values for chart scaling
  const maxRevenue = Math.max(...monthlySales.map(item => item.revenue));
  const maxOrders = Math.max(...monthlySales.map(item => item.orders));

  return (
    <Layout>
      <div className="reports-page">
        {/* Page Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Reports & Analytics</h1>
            <p className="page-subtitle">Track your business performance and growth</p>
          </div>
          <div className="page-actions">
            <button className="btn btn-outline btn-sm">
              <Calendar size={16} />
              {dateRange}
              <ChevronDown size={14} />
            </button>
            <button className="btn btn-outline btn-sm">
              <Download size={16} />
              Export Report
            </button>
            <button className="btn btn-primary">
              <BarChart3 size={18} />
              Generate Report
            </button>
          </div>
        </div>

        {/* Report Type Tabs */}
        <div className="report-tabs">
          <button 
            className={`tab-btn ${reportType === 'Sales' ? 'active' : ''}`}
            onClick={() => setReportType('Sales')}
          >
            <DollarSign size={18} />
            Sales
          </button>
          <button 
            className={`tab-btn ${reportType === 'Orders' ? 'active' : ''}`}
            onClick={() => setReportType('Orders')}
          >
            <ShoppingBag size={18} />
            Orders
          </button>
          <button 
            className={`tab-btn ${reportType === 'Products' ? 'active' : ''}`}
            onClick={() => setReportType('Products')}
          >
            <Package size={18} />
            Products
          </button>
          <button 
            className={`tab-btn ${reportType === 'Customers' ? 'active' : ''}`}
            onClick={() => setReportType('Customers')}
          >
            <Users size={18} />
            Customers
          </button>
        </div>

        {/* Key Metrics */}
        <div className="key-metrics">
          <div className="metric-card">
            <div className="metric-header">
              <span className="metric-label">Total Revenue</span>
              <span className="metric-change up">
                <ArrowUpRight size={14} />
                {salesData.revenueChange}
              </span>
            </div>
            <div className="metric-value">{formatCurrency(salesData.totalRevenue)}</div>
            <div className="metric-footer">
              <span className="metric-sub">vs last month</span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-header">
              <span className="metric-label">Total Orders</span>
              <span className="metric-change up">
                <ArrowUpRight size={14} />
                {salesData.ordersChange}
              </span>
            </div>
            <div className="metric-value">{salesData.totalOrders}</div>
            <div className="metric-footer">
              <span className="metric-sub">vs last month</span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-header">
              <span className="metric-label">Average Order Value</span>
            </div>
            <div className="metric-value">{formatCurrency(salesData.averageOrder)}</div>
            <div className="metric-footer">
              <span className="metric-sub">+6% vs last month</span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-header">
              <span className="metric-label">Conversion Rate</span>
            </div>
            <div className="metric-value">{salesData.conversionRate}</div>
            <div className="metric-footer">
              <span className="metric-sub">+0.4% vs last month</span>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="charts-grid">
          {/* Revenue & Orders Chart */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Revenue & Orders Overview</h3>
                <p className="chart-subtitle">Monthly performance trends</p>
              </div>
              <button className="btn btn-sm btn-outline">View Details</button>
            </div>
            <div className="chart-body">
              <div className="chart-bars revenue-chart">
                <div className="chart-legend">
                  <span><span className="legend-dot revenue"></span> Revenue</span>
                  <span><span className="legend-dot orders"></span> Orders</span>
                </div>
                <div className="bars-container">
                  {monthlySales.map((item, index) => (
                    <div key={index} className="bar-group">
                      <div className="bar-wrapper">
                        <div 
                          className="bar revenue-bar" 
                          style={{ 
                            height: `${(item.revenue / maxRevenue) * 100}%`,
                            background: 'var(--color-primary)'
                          }}
                        />
                        <div 
                          className="bar orders-bar" 
                          style={{ 
                            height: `${(item.orders / maxOrders) * 100}%`,
                            background: 'var(--color-secondary)'
                          }}
                        />
                      </div>
                      <span className="bar-label">{item.month}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Category Performance */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Category Performance</h3>
                <p className="chart-subtitle">Revenue by category</p>
              </div>
              <button className="btn btn-sm btn-outline">View All</button>
            </div>
            <div className="chart-body">
              <div className="category-list">
                {categoryPerformance.map((cat, index) => (
                  <div key={index} className="category-item">
                    <div className="category-info">
                      <span className="category-name">{cat.name}</span>
                      <span className="category-orders">{cat.orders} orders</span>
                    </div>
                    <div className="category-bar-wrapper">
                      <div 
                        className="category-bar" 
                        style={{ 
                          width: `${(cat.revenue / 45000) * 100}%`,
                          background: `hsl(${index * 60 + 120}, 70%, 45%)`
                        }}
                      />
                    </div>
                    <div className="category-stats">
                      <span className="category-revenue">{formatCurrency(cat.revenue)}</span>
                      <span className={`category-growth ${cat.growth.startsWith('+') ? 'up' : 'down'}`}>
                        {cat.growth}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Second Row of Charts */}
        <div className="charts-grid">
          {/* Payment Methods */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Payment Methods</h3>
                <p className="chart-subtitle">Payment distribution</p>
              </div>
            </div>
            <div className="chart-body">
              <div className="payment-methods">
                {paymentMethods.map((method, index) => (
                  <div key={index} className="payment-item">
                    <div className="payment-info">
                      <span className="payment-name">{method.method}</span>
                      <span className="payment-amount">{formatCurrency(method.amount)}</span>
                    </div>
                    <div className="payment-bar-wrapper">
                      <div 
                        className="payment-bar" 
                        style={{ 
                          width: `${method.percentage}%`,
                          background: `hsl(${index * 90 + 180}, 70%, 45%)`
                        }}
                      />
                    </div>
                    <span className="payment-percentage">{method.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Status */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Order Status Distribution</h3>
                <p className="chart-subtitle">Current order status breakdown</p>
              </div>
            </div>
            <div className="chart-body">
              <div className="status-distribution">
                {orderStatus.map((status, index) => {
                  const colors = {
                    'Delivered': 'var(--color-success)',
                    'Out for Delivery': 'var(--color-warning)',
                    'Confirmed': 'var(--color-info)',
                    'Canceled': 'var(--color-danger)'
                  };
                  const icons = {
                    'Delivered': <CheckCircle size={16} />,
                    'Out for Delivery': <Truck size={16} />,
                    'Confirmed': <Clock size={16} />,
                    'Canceled': <XCircle size={16} />
                  };
                  return (
                    <div key={index} className="status-item">
                      <div className="status-info">
                        <span className="status-icon" style={{ color: colors[status.status] }}>
                          {icons[status.status]}
                        </span>
                        <span className="status-name">{status.status}</span>
                      </div>
                      <div className="status-stats">
                        <span className="status-count">{status.count}</span>
                        <span className="status-percentage">{status.percentage}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Top Products */}
        <div className="chart-card full-width">
          <div className="chart-header">
            <div>
              <h3 className="chart-title">Top Performing Products</h3>
              <p className="chart-subtitle">Best selling products by revenue</p>
            </div>
            <button className="btn btn-sm btn-primary">View All Products</button>
          </div>
          <div className="chart-body">
            <div className="table-container">
              <table className="table top-products-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product Name</th>
                    <th>Sales</th>
                    <th>Revenue</th>
                    <th>Growth</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((product, index) => (
                    <tr key={index}>
                      <td>
                        <span className="rank-badge">#{index + 1}</span>
                      </td>
                      <td>
                        <span className="product-name">{product.name}</span>
                      </td>
                      <td>{product.sales} units</td>
                      <td className="product-revenue">{formatCurrency(product.revenue)}</td>
                      <td>
                        <span className={`growth-badge ${product.growth.startsWith('+') ? 'up' : 'down'}`}>
                          {product.growth}
                        </span>
                      </td>
                      <td className="text-right">
                        <button className="btn btn-sm btn-outline">View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Customer Demographics */}
        <div className="chart-card full-width">
          <div className="chart-header">
            <div>
              <h3 className="chart-title">Customer Demographics</h3>
              <p className="chart-subtitle">Customer segmentation analysis</p>
            </div>
          </div>
          <div className="chart-body">
            <div className="demographics-grid">
              {customerDemographics.map((demo, index) => (
                <div key={index} className="demographic-card">
                  <div className="demographic-icon">
                    {demo.type === 'New Customers' && <Users size={24} />}
                    {demo.type === 'Returning Customers' && <Award size={24} />}
                    {demo.type === 'VIP Customers' && <Star size={24} />}
                  </div>
                  <div className="demographic-info">
                    <span className="demographic-type">{demo.type}</span>
                    <span className="demographic-count">{demo.count}</span>
                    <span className="demographic-percentage">{demo.percentage}%</span>
                  </div>
                  <div className="demographic-bar">
                    <div 
                      className="demographic-fill"
                      style={{ 
                        width: `${demo.percentage}%`,
                        background: `hsl(${index * 120 + 200}, 70%, 50%)`
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Reports;